import { useState } from 'react';

import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Card, Dropdown, Flex, Input, Modal, Select, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import { ReactComponent as Search } from '../../../assets/magnifier.svg';
import { ReactComponent as Ellipsis } from '../../../assets/ellipsis.svg';
import { usePagination } from '../../../hooks/usePagination';

import { admitApplicants, batchRejectApplicants, batchResetRejection, downloadStudentUsers, getAllStudentUser } from '../request';
import {
  getAllAcademicSession,
  getAllApplicationBatch,
  getAllModeOfStudy,
  getAllPrograms,
  getAllProgramType,
} from '../../../requests/index';
import styles from '../styles.module.scss';
import { useDebounce } from '../../../hooks/useDebounce';
import { useNavigate } from 'react-router-dom';
import Button from '../../../custom/button/button';
import { useDownload } from '../../../hooks/useDownload';
import TextInput from '../../../custom/input/input';
import { useResetRejectionMutation } from '../../../hooks/api/useResetRejectionMutation';

const StudentUser = () => {
  const { notification } = App.useApp();
  const { currentPage, onChange } = usePagination();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<ApplicantParams>({
    programId: 0,
    modeOfStudyId: 0,
    programTypeId: 0,
    applicationBatchId: 0,
    sessionId: 0,
    name: '',
  });
  const [selectedApplicants, setSelectedApplicants] = useState<React.Key[]>([]);
  const [selectedResetApplicant, setSelectedResetApplicant] = useState<User | null>(null);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [isBulkUndoModalOpen, setIsBulkUndoModalOpen] = useState(false);
  const debouncedName = useDebounce(filters.name || '', 500);
  const { downloadExcelFile } = useDownload();
  const { resetRejectionHandler, isResetRejectionLoading } = useResetRejectionMutation(
    selectedResetApplicant?.applicantId,
    () => setSelectedResetApplicant(null),
  );

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters({ ...filters, name: e.target.value });
  };

  const downloadApplicantsMutation = useMutation({
    mutationFn: downloadStudentUsers,
  });
  const admitApplicantsMutation = useMutation({
    mutationFn: admitApplicants,
  });
  const rejectApplicantMutation = useMutation({
    mutationFn: batchRejectApplicants,
  });
  const bulkUndoMutation = useMutation({
    mutationFn: batchResetRejection,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['get-student-users', currentPage, { ...filters, name: debouncedName }],
    queryFn: () =>
      getAllStudentUser({
        PageNumber: currentPage,
        PageSize: 10,
        programId: filters.programId,
        modeOfStudyId: filters.modeOfStudyId,
        programTypeId: filters.programTypeId,
        applicationBatchId: filters.applicationBatchId,
        sessionId: filters.sessionId,
        name: debouncedName,
      }),
  });

  const [programQuery, modeOfStudyQuery, programTypeQuery, batchQuery, sessionQuery] = useQueries({
    queries: [
      {
        queryKey: ['programs'],
        queryFn: () => getAllPrograms(),
      },
      {
        queryKey: ['mode-of-study'],
        queryFn: getAllModeOfStudy,
      },
      {
        queryKey: ['program-types'],
        queryFn: getAllProgramType,
      },
      {
        queryKey: ['application-batches'],
        queryFn: getAllApplicationBatch,
      },
      {
        queryKey: ['sessions'],
        queryFn: () => getAllAcademicSession(),
      },
    ],
  });

  const programData = programQuery?.data?.data as Program[];
  const modeOfStudyData = modeOfStudyQuery?.data?.data as ModeOfStudy[];
  const programTypeData = programTypeQuery?.data?.data as ProgramType[];
  const batchData = batchQuery?.data?.data as ApplicationBatch[];
  const sessionData = sessionQuery?.data?.data as Session[];

  const programOptions = programData?.map((program) => ({
    label: program.name,
    value: program.id,
  }));
  const modeOfStudyOptions = modeOfStudyData?.map((mode) => ({
    label: mode.name,
    value: mode.id,
  }));
  const programTypeOptions = programTypeData?.map((programType) => ({
    label: programType.name,
    value: programType.id,
  }));

  const batchOptions = batchData?.map((batch) => ({
    label: batch.batchName,
    value: batch.id,
  }));
  const sessionOptions = sessionData?.map((session) => ({
    label: session.name,
    value: session.id,
  }));

  const viewApplicantDetails = (record: User) => {
    navigate(`/applicants/${record.applicantId}`, {
      state: { applicationDetails: record },
    });
  };

  const viewCourseChangeAudit = (record: User) => {
    navigate(`/applicants/${record.applicantId}/course-change-audit`, {
      state: { applicant: record },
    });
  };

  const isRejectedApplicant = (record: User) => String(record?.isAdmitted ?? '').toLowerCase() === 'rejected';

  const columns: ColumnsType<User> = [
    {
      key: 'appNo',
      title: 'Application No',
      dataIndex: 'applicationNumber',
      //render: (_text, _record, index) => (currentPage - 1) * 10 + index + 1,
    },
    {
      key: 'firstName',
      title: 'First Name',
      dataIndex: 'firstName',
    },
    {
      key: 'lastName',
      title: 'Last Name',
      dataIndex: 'lastName',
    },
    {
      key: 'programName',
      title: 'Program Name',
      dataIndex: 'programName',
    },
    {
      key: 'programTypeName',
      title: 'Program Type',
      dataIndex: 'programTypeName',
    },
    {
      key: 'applicationBatchName',
      title: 'Application Batch',
      dataIndex: 'applicationBatchName',
    },
    {
      key: 'modeofStudyName',
      title: 'Mode of Study',
      dataIndex: 'modeofStudyName',
    },
    {
      key: 'isAdmitted',
      title: 'Admission Status',
      dataIndex: 'isAdmitted',
      render: (status) => status || 'Not Available',
    },
    {
      key: 'email',
      title: 'Email',
      dataIndex: 'email',
    },
    {
      key: 'phoneNo',
      title: 'Phone Number',
      dataIndex: 'phoneNumber',
    },
    {
      key: 'action',
      title: 'Action',
      render: (_, record) => {
        const items = [
          {
            label: 'View Details',
            key: 'view-details',
            onClick: () => {
              viewApplicantDetails(record);
            },
          },
          {
            label: 'Course Change Audit',
            key: 'course-change-audit',
            onClick: () => {
              viewCourseChangeAudit(record);
            },
          },
          isRejectedApplicant(record)
            ? {
                label: 'Undo Rejection',
                key: 'undo-rejection',
                onClick: () => {
                  setSelectedResetApplicant(record);
                },
              }
            : null,
        ].filter(Boolean);

        return (
          <Dropdown
            menu={{
              items,
            }}
            trigger={['click']}
          >
            <Ellipsis />
          </Dropdown>
        );
      },
    },
  ];

  const userData = data?.data as User[];
  const userTotal = data?.totalSize as number;

  const handleDownloadApplicants = async () => {
    await downloadApplicantsMutation.mutateAsync(
      { ...filters, name: debouncedName },
      {
        onSuccess: (data) => {
          downloadExcelFile(data as Blob, 'Applicant_Report.xlsx');
        },
      },
    );
  };

  const handleAdmitApplicants = async () => {
    const payload = { applicationNumber: selectedApplicants as string[] };
    await admitApplicantsMutation.mutateAsync(payload, {
      onSuccess: (data) => {
        notification.success({
          message: 'Success',
          description: data?.message || 'Applicants admitted successfully',
        });
        setSelectedApplicants([]);
      },
    });
  };

  const handleRejectApplicants = async (rejectionReason: string, resetForm: () => void) => {
    try {
      await rejectApplicantMutation.mutateAsync(
        {
          applicationNumbers: selectedApplicants as string[],
          reason: rejectionReason,
        },
        {
          onSuccess: (res) => {
            notification.success({
              message: 'Success',
              description: res?.message || `${selectedApplicants.length} applicant(s) rejected successfully`,
            });
            queryClient.invalidateQueries({ queryKey: ['get-student-users'] });
            setSelectedApplicants([]);
            setIsRejectModalOpen(false);
            resetForm();
          },
        },
      );
    } catch (error: any) {
      notification.error({
        message: 'Error',
        description: error?.response?.data?.message || 'Failed to reject applicants',
      });
    }
  };

  const handleBulkUndoRejection = async (reason: string, resetForm: () => void) => {
    const applicantIds = (userData?.filter((u) =>
      selectedApplicants.includes(u.applicationNumber),
    ) ?? []).map((u) => u.applicantId);

    try {
      await bulkUndoMutation.mutateAsync(
        { applicantIds, reason },
        {
          onSuccess: (res) => {
            notification.success({
              message: 'Success',
              description: res?.message || `${applicantIds.length} rejection(s) undone successfully`,
            });
            queryClient.invalidateQueries({ queryKey: ['get-student-users'] });
            setSelectedApplicants([]);
            setIsBulkUndoModalOpen(false);
            resetForm();
          },
        },
      );
    } catch (error: any) {
      notification.error({
        message: 'Error',
        description: error?.response?.data?.message || 'Failed to undo rejections',
      });
    }
  };

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedApplicants(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys: selectedApplicants,
    onChange: onSelectChange,
  };

  const hasSelected = selectedApplicants.length > 0;

  return (
    <div>
      <section className="space-between">
        <h3>Applicants Admission page</h3>
        <Button
          onClick={handleDownloadApplicants}
          text="Download Report"
          isLoading={downloadApplicantsMutation.isPending}
        />
      </section>

      <br />

      <Card bordered={false}>
        <div className={styles.filters}>
          <div>
            <h5>Filters</h5>
            <Flex gap="8px" wrap="wrap">
              <Select
                placeholder="Program"
                allowClear
                options={programOptions}
                loading={programQuery.isLoading}
                onChange={(value) => setFilters({ ...filters, programId: value })}
              />
              <Select
                placeholder="Mode of Study"
                allowClear
                options={modeOfStudyOptions}
                loading={modeOfStudyQuery.isLoading}
                onChange={(value) => setFilters({ ...filters, modeOfStudyId: value })}
              />
              <Select
                placeholder="Degree Type"
                allowClear
                options={programTypeOptions}
                loading={programTypeQuery.isLoading}
                onChange={(value) => setFilters({ ...filters, programTypeId: value })}
              />
              <Select
                placeholder="Application Batch"
                allowClear
                options={batchOptions}
                loading={batchQuery.isLoading}
                onChange={(value) => setFilters({ ...filters, applicationBatchId: value })}
              />
              <Select
                placeholder="Session"
                allowClear
                options={sessionOptions}
                loading={sessionQuery.isLoading}
                onChange={(value) => setFilters({ ...filters, sessionId: value })}
              />
            </Flex>
          </div>

          <Input
            prefix={<Search />}
            placeholder="Search applicant by name"
            onChange={handleSearch}
            allowClear
            className={styles.input}
          />
        </div>
        <br />
        {hasSelected && (
          <Flex justify="space-between" align="center" style={{ marginBottom: 8 }}>
            <Flex gap="8px">
              <Button
                text="Admit Selected Applicants"
                onClick={handleAdmitApplicants}
                isLoading={admitApplicantsMutation.isPending}
                disabled={admitApplicantsMutation.isPending || rejectApplicantMutation.isPending || bulkUndoMutation.isPending}
              />
              <Button
                text="  Deny Admission"
                onClick={() => setIsRejectModalOpen(true)}
                disabled={admitApplicantsMutation.isPending || rejectApplicantMutation.isPending || bulkUndoMutation.isPending}
                style={{ backgroundColor: '#dc2626', color: '#fff' }}
              />
            </Flex>
            <Button
              text="Undo Denied Applicant"
              onClick={() => setIsBulkUndoModalOpen(true)}
              isLoading={bulkUndoMutation.isPending}
              disabled={admitApplicantsMutation.isPending || rejectApplicantMutation.isPending || bulkUndoMutation.isPending}
              style={{ backgroundColor: 'green', color: '#fff' }}

            />
          </Flex>
        )}
        <br />
        <p>
          Showing 1-{userData?.length} of {userTotal}
        </p>

        <Table
          dataSource={userData}
          columns={columns}
          rowKey={(record) => record.applicationNumber}
          rowSelection={rowSelection}
          scroll={{ x: true }}
          loading={isLoading}
          pagination={{
            position: ['bottomCenter'],
            current: currentPage,
            total: userTotal,
            onChange: onChange,
            pageSize: 10,
          }}
        />
      </Card>

      <Modal
        open={isBulkUndoModalOpen}
        onCancel={() => setIsBulkUndoModalOpen(false)}
        footer={null}
        destroyOnClose
        title="Undo Rejection"
      >
        <Formik
          initialValues={{ reason: '' }}
          validationSchema={Yup.object().shape({
            reason: Yup.string().trim().required('Reason is required'),
          })}
          onSubmit={async (values, { resetForm }) => {
            await handleBulkUndoRejection(values.reason.trim(), resetForm);
          }}
        >
          <Form className={styles.verifyForm}>
            <p style={{ marginBottom: 8, color: '#dc2626', fontSize: 13 }}>
              You are about to undo rejection for <strong>{selectedApplicants.length}</strong> applicant(s).
            </p>
            <TextInput
              name="reason"
              label="Reason"
              placeholder="Enter reason for undoing rejection"
              type="textarea"
            />
            <Flex justify="flex-end" gap="12px" className={styles.verifyActions}>
              <Button
                type="button"
                variant="text"
                text="Cancel"
                disabled={bulkUndoMutation.isPending}
                onClick={() => setIsBulkUndoModalOpen(false)}
              />
              <Button
                type="submit"
                text={bulkUndoMutation.isPending ? 'Processing...' : 'Undo Rejection'}
                isLoading={bulkUndoMutation.isPending}
                disabled={bulkUndoMutation.isPending}
                style={{ backgroundColor: 'green', color: '#fff' }}

              />
            </Flex>
          </Form>
        </Formik>
      </Modal>

      <Modal
        open={isRejectModalOpen}
        onCancel={() => setIsRejectModalOpen(false)}
        footer={null}
        destroyOnClose
        title="Reject Applicants"
        style={{ backgroundColor: '#dc2626', color: '#fff' }}
      >
        <Formik
          initialValues={{ rejectionReason: '' }}
          validationSchema={Yup.object().shape({
            rejectionReason: Yup.string().trim().required('Rejection reason is required'),
          })}
          onSubmit={async (values, { resetForm }) => {
            await handleRejectApplicants(values.rejectionReason.trim(), resetForm);
          }}
        >
          <Form className={styles.verifyForm}>
            <p style={{ marginBottom: 8, color: '#dc2626', fontSize: 13 }}>
              You are about to reject <strong>{selectedApplicants.length}</strong> applicant(s).
              This action can be undone per applicant from the action menu.
            </p>
            <TextInput
              name="rejectionReason"
              label="Rejection Reason"
              placeholder="Enter rejection reason"
              type="textarea"
            />
            <Flex justify="flex-end" gap="12px" className={styles.verifyActions}>
              <Button
                type="button"
                variant="text"
                text="Cancel"
                disabled={rejectApplicantMutation.isPending}
                onClick={() => setIsRejectModalOpen(false)}
              />
              <Button
                type="submit"
                text={rejectApplicantMutation.isPending ? 'Rejecting...' : 'Reject'}
                isLoading={rejectApplicantMutation.isPending}
                disabled={rejectApplicantMutation.isPending}
                style={{ backgroundColor: '#dc2626' }}
              />
            </Flex>
          </Form>
        </Formik>
      </Modal>

      <Modal
        open={Boolean(selectedResetApplicant)}
        onCancel={() => setSelectedResetApplicant(null)}
        footer={null}
        destroyOnClose
        title="Undo Rejection"
      >
        {selectedResetApplicant && (
          <Formik
            initialValues={{
              reason: '',
            }}
            validationSchema={Yup.object().shape({
              reason: Yup.string().trim().required('Reason is required'),
            })}
            onSubmit={async (values, { resetForm }) => {
              await resetRejectionHandler(
                {
                  applicantId: selectedResetApplicant.applicantId,
                  reason: values.reason.trim(),
                },
                resetForm,
              );
            }}
          >
            <Form className={styles.verifyForm}>
              <TextInput name="reason" label="Reason" placeholder="Enter reset reason" type="textarea" />
              <Flex justify="flex-end" gap="12px" className={styles.verifyActions}>
                <Button
                  type="button"
                  variant="text"
                  text="Cancel"
                  disabled={isResetRejectionLoading}
                  onClick={() => setSelectedResetApplicant(null)}
                />
                <Button
                  type="submit"
                  text={isResetRejectionLoading ? 'Submitting...' : 'Submit'}
                  isLoading={isResetRejectionLoading}
                  disabled={isResetRejectionLoading}
                />
              </Flex>
            </Form>
          </Formik>
        )}
      </Modal>
    </div>
  );
};

export default StudentUser;
